import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowLeft, Tag, Share2, User } from 'lucide-react';
import { useScreenInit } from '../useScreenInit';
const blogPosts = [{
  id: 1,
  title: '5 Essential Tips for Maintaining Healthy Teeth Between Visits',
  content: `
      <p>Maintaining a healthy smile requires more than just visiting your dentist twice a year. While professional cleanings are essential, the daily habits you practice at home play a significant role in your long-term oral health.</p>
      
      <h2>1. Brush Twice a Day with Fluoride Toothpaste</h2>
      <p>It sounds simple, but many people don't brush for the recommended two minutes. Use a soft-bristled toothbrush and fluoride toothpaste to gently clean all surfaces of your teeth. Fluoride helps strengthen tooth enamel and prevent decay.</p>
      
      <h2>2. Don't Skip the Floss</h2>
      <p>Flossing reaches the areas between your teeth where a toothbrush can't. It removes plaque and food particles that can lead to gum disease and cavities if left untouched. Make it a daily habit before bed.</p>
      
      <h2>3. Clean Your Tongue</h2>
      <p>Plaque can also build up on your tongue, leading to bad breath and other oral health issues. Use a tongue scraper or your toothbrush to gently clean your tongue every time you brush.</p>
      
      <h2>4. Limit Sugary and Acidic Foods</h2>
      <p>Sugar is a primary fuel for the bacteria that cause tooth decay. Acidic foods and drinks can also erode your tooth enamel over time. Try to limit these treats and rinse with water after consuming them.</p>
      
      <h2>5. Stay Hydrated with Water</h2>
      <p>Water is the best drink for your overall health, including your oral health. It helps wash away food particles and keeps your mouth moist, which is essential for neutralizing acids produced by bacteria.</p>
    `,
  category: 'Oral Health',
  date: 'June 15, 2026',
  readTime: '5 min read',
  author: 'Dr. Simba Pari',
  image: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&q=80&w=1200'
}, {
  id: 2,
  title: 'Understanding Invisalign: Is It Right for You?',
  content: `
      <p>If you've been considering orthodontic treatment but aren't thrilled about the idea of traditional metal braces, Invisalign might be the perfect solution for you. This innovative system uses a series of clear, custom-made aligners to gradually straighten your teeth.</p>
      
      <h2>How Invisalign Works</h2>
      <p>The process begins with a 3D digital scan of your teeth. Based on this scan, a personalized treatment plan is created, showing the step-by-step transformation of your smile. You'll receive a new set of aligners every one to two weeks.</p>
      
      <h2>The Benefits of Clear Aligners</h2>
      <p>One of the biggest advantages of Invisalign is that the aligners are virtually invisible. Most people won't even notice you're wearing them. They are also removable, allowing you to eat your favorite foods and brush and floss normally.</p>
      
      <h2>Is It Right for You?</h2>
      <p>Invisalign can treat a wide range of orthodontic issues, including crowding, spacing, and certain bite problems. However, it's essential to have a consultation with an experienced provider to determine if it's the best option for your specific needs.</p>
    `,
  category: 'Orthodontics',
  date: 'June 10, 2026',
  readTime: '7 min read',
  author: 'Dr. Simons',
  image: 'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&q=80&w=1200'
}];
export function BlogPostDetail() {
  useScreenInit();
  const {
    id
  } = useParams();
  const post = blogPosts.find(p => p.id === Number(id)) || blogPosts[0];
  return <div className="min-h-screen bg-white" data-id="element-417">
      <Header data-id="element-418" />

      <main className="pt-40" data-id="element-419">
        {/* Article Hero */}
        <section className="relative h-[60vh] min-h-[400px] overflow-hidden" data-id="element-420">
          <img src={post.image} alt={post.title} className="absolute inset-0 w-full h-full object-cover" data-id="element-421" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#225274] via-[#225274]/40 to-transparent" data-id="element-422" />

          <div className="absolute bottom-0 left-0 right-0 p-4 md:p-12" data-id="element-423">
            <div className="max-w-4xl mx-auto" data-id="element-424">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="space-y-6" data-id="element-425">
                <Link to="/blog" className="inline-flex items-center gap-2 text-white/80 hover:text-white font-bold text-sm transition-colors mb-4" data-id="element-426">
                  <ArrowLeft size={16} data-id="element-427" /> Back to Blog
                </Link>

                <div className="flex flex-wrap gap-4 items-center text-white/90 text-sm font-medium" data-id="element-428">
                  <span className="bg-[#54c595] text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider" data-id="element-429">
                    {post.category}
                  </span>
                  <span className="flex items-center gap-1.5" data-id="element-430">
                    <Calendar size={14} className="text-[#54c595]" data-id="element-431" />
                    {post.date}
                  </span>
                  <span className="flex items-center gap-1.5" data-id="element-432">
                    <Clock size={14} className="text-[#54c595]" data-id="element-433" />
                    {post.readTime}
                  </span>
                </div>

                <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight" data-id="element-434">
                  {post.title}
                </h1>

                <div className="flex items-center gap-4 pt-4 border-t border-white/20" data-id="element-435">
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center border border-white/20" data-id="element-436">
                    <User size={24} className="text-white" data-id="element-437" />
                  </div>
                  <div data-id="element-438">
                    <p className="text-white font-bold" data-id="element-439">{post.author}</p>
                    <p className="text-white/60 text-xs uppercase tracking-widest" data-id="element-440">
                      Clinical Specialist
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-20 px-4" data-id="element-441">
          <div className="max-w-3xl mx-auto" data-id="element-442">
            <div className="prose prose-lg prose-slate max-w-none 
                prose-headings:text-[#225274] prose-headings:font-bold 
                prose-p:text-slate-600 prose-p:leading-relaxed
                prose-strong:text-[#225274]
                prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6" dangerouslySetInnerHTML={{
            __html: post.content
          }} data-id="element-443" />

            {/* Share Section */}
            <div className="mt-16 pt-8 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-6" data-id="element-444">
              <div className="flex items-center gap-4" data-id="element-445">
                <span className="text-sm font-bold text-[#225274] uppercase tracking-widest" data-id="element-446">
                  Share this article:
                </span>
                <div className="flex gap-2" data-id="element-447">
                  {[1, 2, 3].map(i => <button key={i} className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-[#225274] hover:bg-[#54c595] hover:text-white transition-all" data-id="element-448">
                      <Share2 size={18} data-id="element-449" />
                    </button>)}
                </div>
              </div>

              <Link to="/book" className="bg-gradient-to-r from-[#225274] via-[#4bafbc] to-[#54c595] text-white px-8 py-4 rounded-full font-bold uppercase tracking-widest hover:brightness-110 transition-all shadow-lg shadow-teal-900/20" data-id="element-450">
                Book a Consultation
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-451" />
    </div>;
}